import { IMAGE_CONFIG, IMAGE_LOADER, ImageLoaderConfig, NgOptimizedImage, registerLocaleData } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { ErrorHandler, LOCALE_ID, NgModule, provideZonelessChangeDetection } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { NgxPayPalModule } from 'ngx-paypal';
import { NgxStripeModule } from 'ngx-stripe';
import { environment } from "../environments/environment";
import { AddToCartPopupComponent } from './add-to-cart-popup/add-to-cart-popup.component';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ConfirmEmailComponent } from './confirm-email/confirm-email.component';
import { FooterComponent } from './footer/footer.component';
import { HomepageComponent } from './homepage/homepage.component';
import { SizeDetectorComponent } from './html/size-detector/size-detector.component';
import { NavigationComponent } from './navigation/navigation.component';
import { NewsletterComponent } from './newsletter/newsletter.component';
import { BreakAtSpacePipe } from "./pipes/breakAtSpace.pipe";
import { ReversePipe } from "./pipes/reverse.pipe";
import { WagtailButtonClassPipe } from "./pipes/wagtailButtonClass.pipe";
import { WagtailUrlPipe } from "./pipes/wagtailUrl.pipe";
// Product feature is lazy-loaded; its components are declared in ProductModule
import { SharedModule } from './shared/shared.module';
//
import { SearchFieldComponent } from './search/search-field.component';
import { SearchResultsComponent } from "./search/search-results.component";
import { AuthService } from "./services/auth.service";
import { CustomerService } from "./services/customer.service";
import { HttpAuthService } from "./services/http/auth.service";
import { HttpCategoryService } from './services/http/category.service';
import { HttpCouponService } from "./services/http/coupon.service";
import { HttpCustomerService } from "./services/http/customer.service";
import { HttpInterceptorProviders } from "./services/http/interceptors/registry";
import { CsrfService } from "./services/csrf.service";
import { HttpLocationService } from './services/http/location.service';
import { HttpOrderService } from "./services/http/order.service";
import { HttpProductService } from "./services/http/product.service";
import { HttpStockService } from "./services/http/stock.service";
import { HttpStripeService } from "./services/http/stripe.service";
import { HttpWagtailService } from "./services/http/wagtail.service";
import {
  APP_CONFIG,
  CookieConsentModule,
  CookieConsentService,
  HttpPageService,
  NgbdModalContent,
  PageLoadService,
  SEOService,
  SpinnerModule
} from "@gea-waldviertler-website/shop-lib";
import { NotificationService } from "./services/notification.service";
import { OrderService } from "./services/order.service";
import { RootclassService } from "./services/rootclass.service";
import { ToastsContainer } from "./services/toast.service.container";
// Share button is provided via SharedModule
// SVG components are provided via SharedModule
import { CaptionComponent } from './wagtail-page/elements/full-bleed-slider/caption/caption.component';
import { WagtailFullBleedSliderComponent } from './wagtail-page/elements/full-bleed-slider/full-bleed-slider.component';
import { MultiComponent } from './wagtail-page/elements/full-bleed-slider/multi/multi.component';
import { SingleComponent } from './wagtail-page/elements/full-bleed-slider/single/single.component';
import { WagtailHtmlComponent } from './wagtail-page/elements/html/html.component';
import { ProductsComponent } from './wagtail-page/elements/products/products.component';
import { ShopBannerComponent } from "./wagtail-page/elements/shop-banner/shop-banner.component";
import { ShopCardComponent } from "./wagtail-page/elements/shop-card/shop-card.component";
import { WagtailCMSCardsComponent } from "./wagtail-page/elements/shop-cmscards/shop-cmscards.component";
import { ShopFullbleed2ColComponent } from "./wagtail-page/elements/shop-fullbleed2col/shop-fullbleed2col.component";
import { ShopImagesliderComponent } from "./wagtail-page/elements/shop-imageslider/shop-imageslider.component";
import { ShopImageRichtextComponent } from './wagtail-page/elements/shop-imgrte/shop-imgrte.component';
import { ShopNewsletterComponent } from "./wagtail-page/elements/shop-newsletter/shop-newsletter.component";
import { ShopRichtextComponent } from "./wagtail-page/elements/shop-richtext/shop-richtext.component";
import { WagtailTilesComponent } from './wagtail-page/elements/tiles/tiles.component';
import { WagtailTwoColumnsComponent } from "./wagtail-page/elements/two-columns/two-columns.component";
import { WagtailTwoLanComponent } from "./wagtail-page/elements/two-lan/two-lan.component";
import { ShopSimpleTitleComponent } from './wagtail-page/elements/shop-simple-title/shop-simple-title.component';
import * as de from '@angular/common/locales/de';
import { ElasticsearchService } from "./search/elasticsearch.service";
import { HttpSubscriptionsService } from "./services/http/subscriptions.service";
import { SubscriptionsFormComponent } from "./form/subscriptions/subscriptions.component";
import { RefundFormComponent } from "./form/refund/refund.component";
import { FriendlyCaptchaComponent } from "./friendly-captcha/friendly-captcha.component";
import { AddressFormComponent } from "./form/address-form/address-form.component";
import { WagtailAccordionComponent } from './wagtail-page/elements/accordion/accordion.component';
import { FactoryComponent } from './wagtail-page/elements/factory/factory.component';
import { TitleCaseDirective } from "./directives/title-case.directive";
//
import { MatomoService } from "./services/matomo.service";
import { WagtailRichtextComponent } from "./wagtail-page/elements/richtext/richtext.component";
// MapvaluesPipe is provided by SharedModule
import { MatomoInitializationMode, NgxMatomoTrackerModule } from "ngx-matomo-client";
import { Title } from "@angular/platform-browser";
// Shoe width and product structured data are provided by feature/shared modules
import { StructuredDataOrganizationComponent } from "./structured-data/structured-data.organization.component";
// Markdown provided via SharedModule
import { NavigationCategoryAccordionComponent } from "./navigation/category/accordion.component";
import * as Sentry from "@sentry/angular";
import { Router } from "@angular/router";
import { SentrySingletonErrorHandler } from "./sentry-singleton-error-handler";
import { gitCommitHash } from "../environments/git";
import { sentryBeforeSend } from "./helpers";
import { WagtailButtonComponent } from "./wagtail-page/elements/button/button.component";
import { ChangePasswordComponent } from "./change-password/change-password.component";

let sentryProviders: any[] = []
if (environment.hasOwnProperty("sentryDSN")
  && typeof window !== 'undefined'
  && environment.sentryDSN.length) {

  const productionConfig = {
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    // Traces
    tracesSampleRate: 1.0,
  }

  const testConfig = {
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // Traces
    tracesSampleRate: 1.0,
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  }

  Sentry.init({
    dsn: environment.sentryDSN,
    // Adds request headers and IP for users, for more info visit:
    // https://docs.sentry.io/platforms/javascript/guides/angular/configuration/options/#sendDefaultPii
    sendDefaultPii: true,
    release: require('../../package.json').name + "@" + require('../../package.json').version + '+' + gitCommitHash,
    environment: environment.sentryEnvironment,
    beforeSend: sentryBeforeSend,
    beforeSendTransaction: sentryBeforeSend,

    ... environment.production ? productionConfig : testConfig,
  })

  sentryProviders = [
    {
      provide: ErrorHandler,
      useClass: SentrySingletonErrorHandler,
    }, {
      provide: Sentry.TraceService,
      deps: [Router],
    }
  ]
}


@NgModule({
  declarations: [
    AppComponent,
    NavigationComponent,
    NavigationCategoryAccordionComponent,
    NewsletterComponent,
    ToastsContainer,
    ConfirmEmailComponent,
    HomepageComponent,
    WagtailHtmlComponent,
    WagtailFullBleedSliderComponent,
    WagtailRichtextComponent,
    WagtailButtonComponent,
    FactoryComponent,
    WagtailAccordionComponent,
    WagtailUrlPipe,
    ProductsComponent,
    CaptionComponent,
    SingleComponent,
    MultiComponent,
    SizeDetectorComponent,
    FooterComponent,
    AddToCartPopupComponent,
    ReversePipe,
    SearchFieldComponent,
    SearchResultsComponent,
    WagtailTwoColumnsComponent,
    WagtailTwoLanComponent,
    WagtailTilesComponent,
    WagtailCMSCardsComponent,
    ShopRichtextComponent,
    ShopBannerComponent,
    ShopCardComponent,
    ShopNewsletterComponent,
    ShopImagesliderComponent,
    ShopFullbleed2ColComponent,
    WagtailButtonClassPipe,
    BreakAtSpacePipe,
    ShopImageRichtextComponent,
    ShopSimpleTitleComponent,
    SubscriptionsFormComponent,
    RefundFormComponent,
    TitleCaseDirective,
    StructuredDataOrganizationComponent,
    ChangePasswordComponent
  ],
  exports: [],
  bootstrap: [AppComponent],
  imports: [
    SharedModule,
    NgxStripeModule.forRoot(environment.publicStripeKey),
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    NgxPayPalModule,
    BrowserAnimationsModule,
    NgOptimizedImage,
    NgxMatomoTrackerModule.forRoot({
      mode: MatomoInitializationMode.AUTO_DEFERRED,
    }),
    NgbdModalContent,
    CookieConsentModule,
    SpinnerModule,
    FriendlyCaptchaComponent,
    AddressFormComponent
  ], providers: [
        provideZonelessChangeDetection(),
        Title, HttpProductService, HttpCategoryService, HttpStockService, HttpAuthService, AuthService, HttpCouponService,
        HttpCustomerService, HttpInterceptorProviders, HttpOrderService, CustomerService, OrderService, HttpStripeService,
        HttpLocationService, HttpWagtailService, ElasticsearchService, RootclassService, PageLoadService, HttpPageService,
        HttpSubscriptionsService, SEOService, MatomoService, CookieConsentService, NotificationService, CsrfService,
        { provide: LOCALE_ID, useValue: 'de-DE' },
        { provide: APP_CONFIG, useValue: environment },
        {
            provide: IMAGE_CONFIG,
            useValue: {
                breakpoints: [60, 90, 300, 400, 700]
            }
        }, {
            provide: IMAGE_LOADER,
            useValue: (config: ImageLoaderConfig) => {
                if (!config.width) {
                    return environment.imagesURL + "/" + config.src + '.webp';
                }
                return environment.imagesURL + "/" + config.src + '-' + config.width + '.webp';
            },
        },
        provideHttpClient(
          withInterceptorsFromDi(),
          withFetch()
        ),
        ... sentryProviders
    ]})

export class AppModule {
  constructor() {
    registerLocaleData(de.default)
  }
}
